import { WasteScheduleAdherenceStatus, WasteServiceKind } from "generated-graphql/graphql";
import { formatInvoiceDate, NO_VALUE } from "routes/Customer/Waste/Invoices/utils";

export const STATUS_LABELS = {
  [WasteScheduleAdherenceStatus.WithinSchedule]: "Within schedule",
  [WasteScheduleAdherenceStatus.OutsideSchedule]: "Outside schedule",
  [WasteScheduleAdherenceStatus.UnscheduledService]: "Unscheduled service",
  [WasteScheduleAdherenceStatus.NoServiceShown]: "No service shown",
  [WasteScheduleAdherenceStatus.NeedsReview]: "Can't compare",
} satisfies Record<WasteScheduleAdherenceStatus, string>;

export const KIND_LABELS = {
  [WasteServiceKind.WastePickup]: "Retail pickup",
  [WasteServiceKind.VacuumSump]: "Vacuum sump",
  [WasteServiceKind.Sump]: "Sump",
  [WasteServiceKind.PressureTreatedWood]: "Pressure-treated wood",
} satisfies Record<WasteServiceKind, string>;

// Filter options arrive as the raw omnisearch string, so these look-ups are
// keyed by string rather than by the enum (see util/filterOptionLabels.ts).
const STATUS_LABEL_BY_VALUE = new Map<string, string>(Object.entries(STATUS_LABELS));
const KIND_LABEL_BY_VALUE = new Map<string, string>(Object.entries(KIND_LABELS));

/** The status label for a filter option; the raw value when it names no status. */
export const statusOptionLabel = (value: string): string =>
  STATUS_LABEL_BY_VALUE.get(value) ?? value;

/** The service label for a filter option; the raw value when it names no service. */
export const kindOptionLabel = (value: string): string => KIND_LABEL_BY_VALUE.get(value) ?? value;

/**
 * Shown beside every amount, so a money column next to a late visit is not read
 * as an overcharge.
 */
export const ASSOCIATED_BILLING =
  "What these results were billed, never an overcharge: the audit compares service against the schedule, not against a rate.";

/** The invoice's own evidence did not resolve to a kind. */
export const UNKNOWN_KIND_LABEL = "Unclear";

/**
 * How far outside the window, read as English. A window is a range, so "2 days
 * late" means two days after it closed rather than after any particular date.
 */
export function formatDaysOutside(daysOutside: number | null | undefined): string {
  if (daysOutside == null) return NO_VALUE;
  if (daysOutside === 0) return "On time";
  const magnitude = Math.abs(daysOutside);
  const days = magnitude === 1 ? "1 day" : `${magnitude} days`;
  return daysOutside < 0 ? `${days} early` : `${days} late`;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const MONDAY = 1;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Read as the calendar day it spells, in the zone the schedule states it in. */
function utcDay(value: string): Date | null {
  const day = value.slice(0, 10);
  if (!ISO_DAY.test(day)) return null;
  const parsed = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * A vendor week named by its last day: Republic publishes a week-ending date
 * and the Monday is derived, so naming it by the Monday states a commitment the
 * vendor never made.
 */
function namedByLastDay(start: string, end: string): boolean {
  const from = utcDay(start);
  const through = utcDay(end);
  if (from == null || through == null || from.getUTCDay() !== MONDAY) return false;
  const span = through.getTime() - from.getTime();
  return span >= 0 && span < WEEK_MS;
}

/** The window a vendor published: a week for some, a range or a deadline for others. */
export function formatWindow(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  if (!end) return "Not stated";
  if (!start) return `By ${formatInvoiceDate(end)}`;
  if (namedByLastDay(start, end)) return `Week ending ${formatInvoiceDate(end)}`;
  return `${formatInvoiceDate(start)} – ${formatInvoiceDate(end)}`;
}

/**
 * The days the audit graded against, for a window whose name does not state
 * them. Null wherever the name already does, so nothing is said twice.
 */
export function formatGradedSpan(
  start: string | null | undefined,
  end: string | null | undefined
): string | null {
  if (!start || !end || !namedByLastDay(start, end)) return null;
  return `${formatInvoiceDate(start)} – ${formatInvoiceDate(end)}`;
}

/**
 * Facility name with the customer's identifier in brackets, either alone when
 * the other is unknown — the shape the invoice audit prints.
 */
export function facilityLabel(
  facilityName: string | null | undefined,
  customerFacilityId: string | null | undefined
): string | null {
  if (facilityName && customerFacilityId) return `${facilityName} (${customerFacilityId})`;
  return facilityName || customerFacilityId || null;
}
